import {
  Button,
  Checkbox,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Popconfirm,
  Space,
  Spin,
  Tag,
  Upload,
} from "antd";
import { DeleteOutlined, DownloadOutlined, EditOutlined, LeftOutlined, PlusOutlined, RightOutlined, UploadOutlined } from "@ant-design/icons";
import { useEffect, useState } from "react";
import { marked } from "marked";
import MDEditor from "@uiw/react-md-editor";
import "@uiw/react-md-editor/markdown-editor.css";
import { apiUrl } from "../../server";
import { serverFetch } from "../../server";

/** Редактор Markdown для использования в Form (value + onChange от Ant Design). */
const MarkdownEditor = ({ value, onChange, ...rest }) => (
  <MDEditor value={value ?? ""} onChange={(val) => onChange?.(val)} height={200} {...rest} />
);

const Plugin = ({ plugin, user, onSaved, onCancel, onDeleted }) => {
  const [form] = Form.useForm();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [images, setImages] = useState([]);
  const [comments, setComments] = useState([]);
  const [files, setFiles] = useState([]);
  const [loadingImages, setLoadingImages] = useState(false);
  const [loadingComments, setLoadingComments] = useState(false);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [editImageModal, setEditImageModal] = useState(null);
  const [editCommentModal, setEditCommentModal] = useState(null);
  const [dragOverGallery, setDragOverGallery] = useState(false);
  const [dragOverFiles, setDragOverFiles] = useState(false);
  const [previewImageIndex, setPreviewImageIndex] = useState(null);
  const [tags, setTags] = useState([]);
  const [pluginTags, setPluginTags] = useState([]);
  const [loadingTags, setLoadingTags] = useState(false);
  const admin = user?.admin;

  const imagesWithPhoto = images.filter((img) => img.url);
  const previewImage = previewImageIndex != null && imagesWithPhoto[previewImageIndex];

  const loadPlugin = () => {
    if (!plugin?.id) return;
    setLoading(true);
    serverFetch(`Plugin/plugins/${plugin.id}`)
      .then((data) => {
        setItem(data);
        form.setFieldsValue({
          name: data.name,
          act: data.act,
          description: data.description ?? "",
          needPayment: !!data.needPayment,
          idx: data.idx,
          ver: data.ver,
        });
        setLoading(false);
      })
      .catch((e) => {
        setLoading(false);
        message.error(e?.userMessage || "Ошибка загрузки плагина");
      });
  };

  useEffect(() => {
    if (plugin?.id) loadPlugin();
    else if (plugin) {
      setItem(plugin);
      form.setFieldsValue({
        name: plugin.name ?? "",
        act: plugin.act ?? true,
        description: plugin.description ?? "",
        needPayment: Boolean(plugin.needPayment),
        idx: plugin.idx ?? 0,
        ver: plugin.ver ?? 1,
      });
      setLoading(false);
    }
  }, [plugin?.id, plugin]);

  const loadImages = () => {
    if (!plugin?.id) return;
    setLoadingImages(true);
    serverFetch("Plugin/images", { queryParams: { pluginId: plugin.id } })
      .then((data) => setImages(Array.isArray(data) ? data : []))
      .catch(() => setImages([]))
      .finally(() => setLoadingImages(false));
  };

  const loadComments = () => {
    if (!plugin?.id) return;
    setLoadingComments(true);
    serverFetch("Plugin/comments", { queryParams: { pluginId: plugin.id } })
      .then((data) => setComments(Array.isArray(data) ? data : []))
      .catch(() => setComments([]))
      .finally(() => setLoadingComments(false));
  };

  const loadFiles = () => {
    if (!plugin?.id) return;
    setLoadingFiles(true);
    serverFetch(`Plugin/files/list/${plugin.id}`)
      .then((data) => setFiles(Array.isArray(data) ? data : []))
      .catch(() => setFiles([]))
      .finally(() => setLoadingFiles(false));
  };

  const loadTags = (progId) => {
    if (!progId) return;
    setLoadingTags(true);
    serverFetch("Plugin/tags", { queryParams: { progId } })
      .then((data) => setTags(Array.isArray(data) ? data : []))
      .catch(() => setTags([]))
      .finally(() => setLoadingTags(false));
  };

  const loadPluginTags = (pluginId) => {
    if (!pluginId) return;
    serverFetch("Plugin/plugintags", { queryParams: { pluginId } })
      .then((data) => setPluginTags(Array.isArray(data) ? data : []))
      .catch(() => setPluginTags([]));
  };

  const addPluginTag = (tagId) => {
    if (!item?.id) return;
    serverFetch("Plugin/plugintags", { method: "POST", bodyData: { pluginId: item.id, tagId } })
      .then(() => {
        setPluginTags((prev) => [...prev, { pluginId: item.id, tagId }]);
        message.success("Метка добавлена");
      })
      .catch((e) => message.error(e?.userMessage || "Ошибка"));
  };

  const removePluginTag = (tagId) => {
    if (!item?.id) return;
    serverFetch("Plugin/plugintags", { method: "DELETE", queryParams: { pluginId: item.id, tagId } })
      .then(() => setPluginTags((prev) => prev.filter((pt) => pt.tagId !== tagId)))
      .catch((e) => message.error(e?.userMessage || "Ошибка"));
  };

  useEffect(() => {
    loadImages();
    loadComments();
    loadFiles();
  }, [plugin?.id]);

  useEffect(() => {
    if (item?.id) {
      loadTags(item.progId);
      loadPluginTags(item.id);
    } else {
      setTags([]);
      setPluginTags([]);
    }
  }, [item?.id, item?.progId]);

  const onValuesChange = (_, values) => setItem((prev) => (prev ? { ...prev, ...values } : null));

  const onSavePlugin = () => {
    if (!item) return;
    form.validateFields().then(() => {
      setSaving(true);
      const isNew = !item.id;
      const method = isNew ? "POST" : "PUT";
      const payload = { ...item, needPayment: Boolean(item.needPayment) };
      serverFetch("Plugin/plugins", { method, bodyData: payload })
        .then((data) => {
          setSaving(false);
          message.success(isNew ? "Плагин добавлен" : "Плагин сохранён");
          onSaved?.(data);
        })
        .catch((e) => {
          setSaving(false);
          message.error(e?.userMessage || "Ошибка сохранения");
        });
    });
  };

  const onDeletePlugin = () => {
    if (!item?.id) return;
    setDeleting(true);
    serverFetch(`Plugin/plugins/${item.id}`, { method: "DELETE" })
      .then(() => {
        setDeleting(false);
        message.success("Плагин удалён");
        onDeleted?.();
      })
      .catch((e) => {
        setDeleting(false);
        message.error(e?.userMessage || "Ошибка удаления");
      });
  };

  const addImage = () => {
    const newImg = { pluginId: plugin.id, name: "", idx: images.length, url: "", description: "" };
    setLoadingImages(true);
    serverFetch("Plugin/images", { method: "POST", bodyData: newImg })
      .then((data) => {
        setImages((prev) => [...prev, data]);
        setEditImageModal(data);
        setLoadingImages(false);
      })
      .catch((e) => {
        setLoadingImages(false);
        message.error(e?.userMessage || "Ошибка добавления");
      });
  };

  const updateImage = (img) => {
    setLoadingImages(true);
    serverFetch("Plugin/images", { method: "PUT", bodyData: img })
      .then(() => {
        loadImages();
        setEditImageModal(null);
        setLoadingImages(false);
      })
      .catch((e) => {
        setLoadingImages(false);
        message.error(e?.userMessage || "Ошибка сохранения");
      });
  };

  const deleteImage = (id) => {
    setLoadingImages(true);
    serverFetch(`Plugin/images/${id}`, { method: "DELETE" })
      .then(() => loadImages())
      .catch((e) => {
        setLoadingImages(false);
        message.error(e?.userMessage || "Ошибка удаления");
      });
  };

  const uploadImagePhoto = (imageId, file, silent = false) => {
    const formData = new FormData();
    formData.append("file", file);
    const sessionId = localStorage.getItem("sessionId");
    return fetch(`${apiUrl}/api/Plugin/imagesphoto/${imageId}`, {
      method: "POST",
      headers: sessionId ? { SessionId: sessionId } : {},
      body: formData,
    })
      .then((r) => (r.ok ? r.json() : r.json().then((e) => Promise.reject(e))))
      .then(() => {
        if (!silent) {
          loadImages();
          message.success("Фото загружено");
        }
      })
      .catch((e) => message.error(e?.userMessage || e?.message || "Ошибка загрузки"));
  };

  const handleGalleryDrop = (e) => {
    e.preventDefault();
    setDragOverGallery(false);
    if (!admin) return;
    const files = Array.from(e.dataTransfer.files).filter((f) => f.type.startsWith("image/"));
    if (!files.length) {
      message.info("Перетащите файлы изображений");
      return;
    }
    setLoadingImages(true);
    const baseIdx = images.length;
    const runNext = (index) => {
      if (index >= files.length) {
        loadImages();
        setLoadingImages(false);
        message.success(`Загружено фото: ${files.length}`);
        return;
      }
      const file = files[index];
      const body = {
        pluginId: plugin.id,
        name: file.name || "",
        idx: baseIdx + index,
        url: "",
        description: "",
      };
      serverFetch("Plugin/images", { method: "POST", bodyData: body })
        .then((created) => {
          setImages((prev) => [...prev, created]);
          return uploadImagePhoto(created.id, file, true);
        })
        .then(() => runNext(index + 1))
        .catch((err) => {
          setLoadingImages(false);
          message.error(err?.userMessage || "Ошибка загрузки");
        });
    };
    runNext(0);
  };

  const handleFilesDrop = (e) => {
    e.preventDefault();
    setDragOverFiles(false);
    if (!admin) return;
    const files = Array.from(e.dataTransfer.files);
    if (!files.length) return;
    files.forEach((file) => uploadFile(file));
  };

  const addComment = () => {
    setEditCommentModal({
      pluginId: plugin.id,
      authorId: user?.id ?? 0,
      forAdmin: false,
      body: "",
    });
  };

  const saveComment = (comment) => {
    setLoadingComments(true);
    const isNew = !comment.id;
    const method = isNew ? "POST" : "PUT";
    serverFetch("Plugin/comments", { method, bodyData: comment })
      .then(() => {
        loadComments();
        setEditCommentModal(null);
        setLoadingComments(false);
        message.success(isNew ? "Комментарий добавлен" : "Комментарий сохранён");
      })
      .catch((e) => {
        setLoadingComments(false);
        message.error(e?.userMessage || (isNew ? "Ошибка добавления" : "Ошибка сохранения"));
      });
  };

  const updateComment = (comment) => saveComment(comment);

  const deleteComment = (id) => {
    setLoadingComments(true);
    serverFetch(`Plugin/comments/${id}`, { method: "DELETE" })
      .then(() => loadComments())
      .catch((e) => {
        setLoadingComments(false);
        message.error(e?.userMessage || "Ошибка удаления");
      });
  };

  const uploadFile = (file) => {
    const formData = new FormData();
    formData.append("file", file);
    const sessionId = localStorage.getItem("sessionId");
    fetch(`${apiUrl}/api/Plugin/files/${plugin.id}`, {
      method: "POST",
      headers: sessionId ? { SessionId: sessionId } : {},
      body: formData,
    })
      .then((r) => (r.ok ? loadFiles() : r.json().then((e) => Promise.reject(e))))
      .then(() => message.success("Файл загружен"))
      .catch((e) => message.error(e?.userMessage || e?.message || "Ошибка загрузки"));
    return false;
  };

  const deleteFile = (fileName) => {
    setLoadingFiles(true);
    serverFetch(`Plugin/files/${plugin.id}/${encodeURIComponent(fileName)}`, { method: "DELETE" })
      .then(() => loadFiles())
      .catch((e) => {
        setLoadingFiles(false);
        message.error(e?.userMessage || "Ошибка удаления");
      });
  };

  const downloadFile = (fileName) => {
    const sessionId = localStorage.getItem("sessionId");
    const url = `${apiUrl}/api/Plugin/files/${plugin.id}/${encodeURIComponent(fileName)}`;
    fetch(url, { headers: sessionId ? { SessionId: sessionId } : {} })
      .then((r) => {
        if (!r.ok) return r.json().then((e) => Promise.reject(e));
        return r.blob();
      })
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = fileName;
        a.click();
        URL.revokeObjectURL(a.href);
      })
      .catch((e) => message.error(e?.userMessage || e?.message || "Ошибка скачивания"));
  };

  /** Имя файла из заголовка Content-Disposition (сервер присылает, например: plugin_{Name}_{Ver}.zip). */
  const getFileNameFromResponse = (response) => {
    const disposition = response.headers.get("Content-Disposition");
    if (!disposition) return null;
    const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8Match) return decodeURIComponent(utf8Match[1].trim());
    const quotedMatch = disposition.match(/filename="([^"]*)"/i);
    if (quotedMatch) return quotedMatch[1].trim();
    const unquotedMatch = disposition.match(/filename=([^;\s]+)/i);
    return unquotedMatch ? unquotedMatch[1].trim() : null;
  };

  const downloadAllFiles = () => {
    const sessionId = localStorage.getItem("sessionId");
    const url = `${apiUrl}/api/Plugin/files/pack/${plugin.id}`;
    fetch(url, { headers: sessionId ? { SessionId: sessionId } : {} })
      .then((r) => {
        if (!r.ok) return r.json().then((e) => Promise.reject(e));
        return r.blob().then((blob) => ({ blob, response: r }));
      })
      .then(({ blob, response }) => {
        const zipName = getFileNameFromResponse(response) ?? `plugin_${plugin.id}_${item?.ver ?? 1}.zip`;
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = zipName;
        a.click();
        URL.revokeObjectURL(a.href);
        message.success("Архив скачан");
      })
      .catch((e) => message.error(e?.userMessage || e?.message || "Ошибка скачивания архива"));
  };

  if (!plugin) return null;
  if (plugin.id && (loading || !item)) return <div style={{ textAlign: "center", padding: 48 }}><Spin /></div>;
  if (!plugin.id && !item) return null;

  const hasDescription = item?.description != null && String(item.description).trim() !== "";
  const hasSubscription = user?.support;

  return (
    <>
      {(admin || hasDescription) && (
      <div className="plugins-detail-section">
        {admin && <h3>Плагин</h3>}
        <Form form={form} layout="vertical" onValuesChange={onValuesChange} disabled={!admin}>
          <div className="plugins-form-row-inline">
            {admin && (
              <>
                <Form.Item name="act" valuePropName="checked">
                  <Checkbox>Активен</Checkbox>
                </Form.Item>
                <Form.Item name="needPayment" valuePropName="checked">
                  <Checkbox>За плату</Checkbox>
                </Form.Item>
                <Form.Item name="idx" className="plugins-form-item-inline">
                  <div className="plugins-inline-label-control">
                    <span className="plugins-inline-label">Порядок</span>
                    <InputNumber min={0} style={{ width: 72, minWidth: 72 }} />
                  </div>
                </Form.Item>
                <Form.Item name="ver" className="plugins-form-item-inline">
                  <div className="plugins-inline-label-control">
                    <span className="plugins-inline-label">Версия</span>
                    <InputNumber min={0} style={{ width: 72, minWidth: 72 }} />
                  </div>
                </Form.Item>
              </>
            )}
          </div>
          {admin && (
            <Form.Item name="name" label="Наименование" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
          )}
          {admin ? (
            <Form.Item name="description" label="Описание">
              <MarkdownEditor placeholder="Поддерживается Markdown" />
            </Form.Item>
          ) : (
            hasDescription && (
              <div className="plugins-description-wrap">
                <div
                  className="plugins-description-markdown"
                  dangerouslySetInnerHTML={{ __html: marked.parse(String(item?.description ?? "")) }}
                />
              </div>
            )
          )}
          {admin && (
            <Form.Item>
              <Space>
                <Button onClick={onCancel}>Отмена</Button>
                {item?.id && (
                  <Popconfirm title="Удалить плагин?" onConfirm={onDeletePlugin} okText="Да" cancelText="Нет">
                    <Button danger loading={deleting}>Удалить</Button>
                  </Popconfirm>
                )}
                <Button type="primary" onClick={onSavePlugin} loading={saving}>Сохранить</Button>
              </Space>
            </Form.Item>
          )}
        </Form>
      </div>
      )}

      {item?.id && (
        <>
      {!admin && (item?.tags ?? plugin?.tags)?.length > 0 && (
      <div className="plugins-detail-section">
        <h3>Метки</h3>
        <div className="plugins-tags-list plugins-tags-list-readonly">
          {(item?.tags ?? plugin?.tags).map((t) => (
            <Tag key={t.id} color={t.color || undefined}>{t.name}</Tag>
          ))}
        </div>
      </div>
      )}
      {admin && tags.length > 0 && (
      <div className="plugins-detail-section">
        <h3>Метки</h3>
        {loadingTags ? (
          <Spin />
        ) : (
          <div className="plugins-tags-list">
            {tags.map((tag) => {
              const checked = pluginTags.some((pt) => pt.tagId === tag.id);
              return (
                <Checkbox
                  key={tag.id}
                  checked={checked}
                  onChange={(e) => (e.target.checked ? addPluginTag(tag.id) : removePluginTag(tag.id))}
                  disabled={!admin}
                >
                  {tag.name}
                </Checkbox>
              );
            })}
          </div>
        )}
      </div>
      )}

      {(admin || images.length > 0) && (
      <div className="plugins-detail-section">
        <h3>Галерея</h3>
        {loadingImages ? (
          <Spin />
        ) : (
          <>
            <div
              className={`plugins-gallery-drop ${dragOverGallery ? "plugins-drop-over" : ""}`}
              onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); admin && setDragOverGallery(true); }}
              onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setDragOverGallery(false); }}
              onDrop={handleGalleryDrop}
            >
            <div className="plugins-gallery">
              {images.filter((img) => admin || img.url).map((img) => (
                <div key={img.id} className="plugins-gallery-item">
                  {img.url ? (
                    <img
                      src={img.url}
                      alt={img.name || ""}
                      className="plugins-gallery-thumb"
                      onClick={() => {
                        const idx = imagesWithPhoto.findIndex((i) => i.id === img.id);
                        if (idx !== -1) setPreviewImageIndex(idx);
                      }}
                    />
                  ) : (
                    <div style={{ width: 120, height: 120, background: "#f0f0f0", borderRadius: 4, display: "flex", alignItems: "center", justifyContent: "center" }}>Нет фото</div>
                  )}
                  {admin && (
                    <Space style={{ marginTop: 4 }}>
                      <Button type="link" size="small" icon={<EditOutlined />} onClick={() => setEditImageModal(img)} />
                      <Popconfirm title="Удалить?" onConfirm={() => deleteImage(img.id)}>
                        <Button type="link" size="small" danger icon={<DeleteOutlined />} />
                      </Popconfirm>
                      <Upload showUploadList={false} beforeUpload={(f) => uploadImagePhoto(img.id, f)} accept="image/*">
                        <Button type="link" size="small" icon={<UploadOutlined />} title="Загрузить фото" />
                      </Upload>
                    </Space>
                  )}
                </div>
              ))}
            </div>
            {admin && (
              <p className="plugins-drop-hint">Перетащите изображения сюда или</p>
            )}
            {admin && <Button type="dashed" onClick={addImage} icon={<PlusOutlined />} style={{ marginTop: 8 }}>Добавить картинку</Button>}
            </div>
          </>
        )}
      </div>
      )}

      {(admin || comments.length > 0) && (
      <div className="plugins-detail-section">
        <h3>Комментарии</h3>
        {loadingComments ? (
          <Spin />
        ) : (
          <>
            <ul className="plugins-comments-list">
              {comments.map((c) => (
                <li key={c.id}>
                  <div className="plugins-comment-meta">
                    <span>ID {c.id}, автор {c.authorId}, {c.forAdmin ? "для админа" : ""}, {c.createdOn ? new Date(c.createdOn).toLocaleString() : ""}</span>
                    {admin && (
                      <Space>
                        <Button type="link" size="small" icon={<EditOutlined />} onClick={() => setEditCommentModal(c)} />
                        <Popconfirm title="Удалить?" onConfirm={() => deleteComment(c.id)}>
                          <Button type="link" size="small" danger icon={<DeleteOutlined />} />
                        </Popconfirm>
                      </Space>
                    )}
                  </div>
                  {c.body != null && String(c.body).trim() !== "" && (
                    <div
                      className="plugins-comment-body plugins-description-markdown"
                      dangerouslySetInnerHTML={{ __html: marked.parse(String(c.body)) }}
                    />
                  )}
                </li>
              ))}
            </ul>
            {admin && <Button type="dashed" onClick={addComment} icon={<PlusOutlined />} style={{ marginTop: 8 }}>Добавить комментарий</Button>}
          </>
        )}
      </div>
      )}

      {(admin || (files.length > 0 && !item?.needPayment)) && (
      <div className="plugins-detail-section">
        <h3>Файлы</h3>
        {loadingFiles ? (
          <Spin />
        ) : (
          <>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={downloadAllFiles}
              disabled={!hasSubscription}
              style={{ marginBottom: 12 }}
            >
              Скачать
            </Button>
            <div
              className={`plugins-files-drop ${dragOverFiles ? "plugins-drop-over" : ""}`}
              onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); admin && setDragOverFiles(true); }}
              onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setDragOverFiles(false); }}
              onDrop={handleFilesDrop}
            >
            <ul className="plugins-files-list">
              {files.map((f) => (
                <li key={f.fileName}>
                  <span
                    className={hasSubscription ? "plugins-file-download" : "plugins-file-download plugins-file-download-disabled"}
                    onClick={hasSubscription ? () => downloadFile(f.fileName) : undefined}
                    role={hasSubscription ? "button" : undefined}
                    tabIndex={hasSubscription ? 0 : undefined}
                    onKeyDown={hasSubscription ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); downloadFile(f.fileName); } } : undefined}
                  >
                    {f.fileName} ({f.size != null ? `${(f.size / 1024).toFixed(1)} КБ` : ""})
                  </span>
                  {admin && (
                    <Popconfirm title="Удалить файл?" onConfirm={() => deleteFile(f.fileName)}>
                      <Button type="link" size="small" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                  )}
                </li>
              ))}
            </ul>
            {admin && (
              <>
                <p className="plugins-drop-hint">Перетащите файлы сюда или</p>
                <Upload showUploadList={false} beforeUpload={uploadFile} style={{ marginTop: 8 }}>
                  <Button type="dashed" icon={<PlusOutlined />}>Загрузить файл</Button>
                </Upload>
              </>
            )}
            </div>
          </>
        )}
      </div>
      )}
        </>
      )}

      {previewImage && (
        <Modal
          title={previewImage.name || "Фото"}
          open
          zIndex={1100}
          onCancel={() => setPreviewImageIndex(null)}
          footer={null}
          width="min(90vw, 900px)"
          className="plugins-preview-modal"
        >
          <div className="plugins-preview-wrap">
            <Button
              type="text"
              className="plugins-preview-prev"
              icon={<LeftOutlined />}
              disabled={previewImageIndex <= 0}
              onClick={() => setPreviewImageIndex((i) => Math.max(0, i - 1))}
            />
            <img src={previewImage.url} alt={previewImage.name || ""} className="plugins-preview-img" />
            <Button
              type="text"
              className="plugins-preview-next"
              icon={<RightOutlined />}
              disabled={previewImageIndex >= imagesWithPhoto.length - 1}
              onClick={() => setPreviewImageIndex((i) => Math.min(imagesWithPhoto.length - 1, i + 1))}
            />
          </div>
          <div className="plugins-preview-counter">
            {previewImageIndex + 1} / {imagesWithPhoto.length}
          </div>
          <div className="plugins-preview-description">
            <span className="plugins-preview-description-label">Описание</span>
            {previewImage.description ? (
              <div
                className="plugins-preview-description-markdown"
                dangerouslySetInnerHTML={{ __html: marked.parse(previewImage.description) }}
              />
            ) : (
              "—"
            )}
          </div>
        </Modal>
      )}

      {editImageModal && (
        <Modal
          title="Картинка"
          open
          zIndex={1100}
          onCancel={() => setEditImageModal(null)}
          onOk={() => updateImage(editImageModal)}
          okText="Сохранить"
        >
          <Form layout="vertical" initialValues={editImageModal} onValuesChange={(_, v) => setEditImageModal((p) => ({ ...p, ...v }))}>
            <Form.Item label="Наименование" name="name"><Input /></Form.Item>
            <Form.Item label="Порядок" name="idx"><InputNumber min={0} style={{ width: "100%" }} /></Form.Item>
            <Form.Item label="Описание" name="description"><MarkdownEditor /></Form.Item>
          </Form>
        </Modal>
      )}

      {editCommentModal && (
        <Modal
          title="Комментарий"
          open
          zIndex={1100}
          onCancel={() => setEditCommentModal(null)}
          onOk={() => updateComment(editCommentModal)}
          okText="Сохранить"
        >
          <Form
            key={editCommentModal.id ?? "new"}
            layout="vertical"
            initialValues={{ forAdmin: !!editCommentModal.forAdmin, body: editCommentModal.body ?? "" }}
            onValuesChange={(_, v) => setEditCommentModal((p) => ({ ...p, ...v }))}
          >
            <Form.Item label="Текст комментария" name="body">
              <MarkdownEditor />
            </Form.Item>
            <Form.Item label="Для администратора" name="forAdmin" valuePropName="checked"><Checkbox /></Form.Item>
          </Form>
        </Modal>
      )}
    </>
  );
};

export default Plugin;
